import { type FC, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/app-store';
import { classifyThreat } from '@/algorithms/threat-classifier';
import { timeAgo, truncate } from '@/utils/format';
import type { ThreatLevel, EventType } from '@/types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEV_STYLES: Record<ThreatLevel, string> = {
  critical: 'bg-red-900/60 text-red-400 border border-red-800/50',
  high:     'bg-orange-900/60 text-orange-400 border border-orange-800/50',
  medium:   'bg-yellow-900/50 text-yellow-400 border border-yellow-800/40',
  low:      'bg-blue-900/50 text-blue-400 border border-blue-800/40',
  info:     'bg-slate-800/60 text-slate-500 border border-slate-700/40',
};

const SEV_LABEL: Record<ThreatLevel, string> = {
  critical: 'CRIT',
  high:     'HIGH',
  medium:   'MED',
  low:      'LOW',
  info:     'INFO',
};

const CAT_ICON: Partial<Record<EventType, string>> = {
  military:      '⚔',
  cyber:         '⬡',
  economic:      '↘',
  diplomatic:    '◈',
  disaster:      '⚡',
  terrorism:     '◉',
  health:        '✚',
  environmental: '◈',
  crime:         '◆',
  tech:          '⬡',
  conflict:      '⚔',
  protest:       '◈',
};

type FilterLevel = 'all' | ThreatLevel;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const NewsFeedPanel: FC = () => {
  const { t } = useTranslation();
  const news = useAppStore((s) => s.news);
  const [filter, setFilter] = useState<FilterLevel>('all');

  // Resolve severity for each item (fallback to classifier)
  const enriched = useMemo(
    () =>
      news.slice(0, 120).map((n) => {
        const severity: ThreatLevel = n.severity ?? classifyThreat(n.title).level;
        const category: EventType | undefined =
          n.category ?? (classifyThreat(n.title).category as EventType | undefined);
        return { ...n, severity, category };
      }),
    [news],
  );

  const filtered = useMemo(
    () => (filter === 'all' ? enriched : enriched.filter((n) => n.severity === filter)),
    [enriched, filter],
  );

  // Counts per level for badges
  const counts = useMemo(() => {
    const c: Record<FilterLevel, number> = { all: enriched.length, critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    for (const n of enriched) c[n.severity] = (c[n.severity] ?? 0) + 1;
    return c;
  }, [enriched]);

  const FILTERS: { id: FilterLevel; label: string }[] = [
    { id: 'all',      label: `All ${counts.all}` },
    { id: 'critical', label: `CRIT ${counts.critical}` },
    { id: 'high',     label: `HIGH ${counts.high}` },
    { id: 'medium',   label: `MED ${counts.medium}` },
    { id: 'low',      label: `LOW ${counts.low}` },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="panel-header shrink-0">
        <span className="panel-title">{t('panels.newsFeed')}</span>
        <span className="text-[10px] tabular-nums text-slate-500">{filtered.length}/{news.length}</span>
      </div>

      {/* Filter chips */}
      <div className="flex gap-1 px-2 py-1.5 shrink-0 border-b border-surface-border">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={[
              'text-[9px] px-1.5 py-0.5 rounded font-mono font-semibold transition-colors',
              filter === f.id
                ? f.id === 'critical'
                  ? 'bg-red-900/70 text-red-300'
                  : f.id === 'high'
                  ? 'bg-orange-900/70 text-orange-300'
                  : f.id === 'medium'
                  ? 'bg-yellow-900/60 text-yellow-300'
                  : f.id === 'low'
                  ? 'bg-blue-900/60 text-blue-300'
                  : 'bg-slate-700 text-slate-200'
                : 'text-slate-500 hover:text-slate-400',
            ].join(' ')}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Feed */}
      <div className="panel-body flex-1 overflow-y-auto scrollbar-thin">
        {filtered.length === 0 && (
          <p className="text-xs text-slate-500 text-center py-4">Awaiting feed data...</p>
        )}
        {filtered.map((n) => (
          <a
            key={n.id}
            href={n.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block px-2 py-2 border-b border-slate-800/40 hover:bg-slate-800/30 transition-colors"
          >
            {/* Row 1: severity + category + title */}
            <div className="flex items-start gap-1.5">
              <span className={`shrink-0 text-[8px] font-bold px-1 py-0.5 rounded font-mono mt-0.5 ${SEV_STYLES[n.severity]}`}>
                {SEV_LABEL[n.severity]}
              </span>
              {n.category && CAT_ICON[n.category] && (
                <span className="shrink-0 text-[10px] text-slate-600 mt-0.5">{CAT_ICON[n.category]}</span>
              )}
              <span className="text-[11px] text-slate-200 leading-snug">{truncate(n.title, 90)}</span>
            </div>

            {/* Row 2: description (if available) */}
            {n.description && (
              <p className="text-[10px] text-slate-500 leading-snug mt-0.5 pl-[42px]">
                {truncate(n.description, 110)}
              </p>
            )}

            {/* Row 3: meta */}
            <div className="flex items-center gap-1.5 mt-0.5 pl-[42px]">
              <span className="text-[9px] text-slate-600">{n.source}</span>
              {n.countryCode && (
                <>
                  <span className="text-[9px] text-slate-700">·</span>
                  <span className="text-[9px] text-slate-500 font-mono">{n.countryCode}</span>
                </>
              )}
              <span className="text-[9px] text-slate-700">·</span>
              <span className="text-[9px] text-slate-600">{timeAgo(n.publishedAt)}</span>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};
